import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ExternalLink, RefreshCcw } from "lucide-react";
import { useAppDispatch } from "@/stores/hook";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import {
  dismissRecaptureAlert,
  useRecaptureAlertBatch,
} from "./recaptureAlert";

const SHOWN_LEADS_CAP = 6;

/**
 * Global popup shown to a lead's assignees when that lead RE-ENQUIRES
 * (comes in again through a source). Mounted once in AppLayout (needs the
 * router for "View lead"); shows whatever batch the coalescer flushed —
 * one lead, or a list when a sync run recaptured several at once.
 */
export default function RecaptureAlertHost() {
  const batch = useRecaptureAlertBatch();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  if (!batch?.length) return null;

  const single = batch.length === 1 ? batch[0] : null;

  const openLead = (leadId: number) => {
    dismissRecaptureAlert();
    // Let the dialog's close animation eat the click tail before the lead
    // sheet mounts (same 16ms trick as ShuffleWarningModal/NotificationItem).
    window.setTimeout(() => {
      dispatch(setCurrentLeadId(leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && dismissRecaptureAlert()}>
      <DialogContent className="sm:max-w-[440px] rounded-xl">
        <DialogHeader>
          <p className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span className="flex size-8 items-center justify-center rounded-full bg-[#fff4ec]">
              <RefreshCcw className="size-4 text-[#ff8447]" />
            </span>
            {single
              ? "Lead re-enquired!"
              : `${batch.length} of your leads re-enquired`}
          </p>
          <Separator />
          <DialogDescription className="pt-2 text-[13px] text-[#505e59]" asChild>
            <div>
              {single ? (
                <p className="m-0">
                  <span className="font-semibold text-[#1e302a]">
                    {single.leadName || "A lead assigned to you"}
                  </span>
                  {single.phone ? ` (${single.phone})` : ""} just enquired again
                  {single.sourceName ? (
                    <>
                      {" "}via{" "}
                      <span className="font-medium">{single.sourceName}</span>
                    </>
                  ) : null}
                  .{" "}
                  They're showing fresh interest — reach out while it's warm.
                </p>
              ) : (
                <div>
                  <p className="m-0 mb-2">
                    These leads assigned to you just enquired again:
                  </p>
                  <div className="flex flex-col gap-1">
                    {batch.slice(0, SHOWN_LEADS_CAP).map((p) => (
                      <button
                        key={p.leadId}
                        type="button"
                        onClick={() => openLead(p.leadId)}
                        className="flex items-center gap-1.5 text-[12.5px] text-left text-[#051912] hover:text-[#156548] transition-colors group"
                      >
                        <ExternalLink className="size-3 text-[#ff8447] group-hover:text-[#156548] shrink-0" />
                        <span className="font-medium truncate">
                          {p.leadName || `Lead #${p.leadId}`}
                        </span>
                        {p.sourceName && (
                          <span className="text-[#697571] shrink-0">
                            · {p.sourceName}
                          </span>
                        )}
                      </button>
                    ))}
                    {batch.length > SHOWN_LEADS_CAP && (
                      <span className="text-[11px] text-[#697571]">
                        +{batch.length - SHOWN_LEADS_CAP} more — check your
                        notifications
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={dismissRecaptureAlert}>
            Dismiss
          </Button>
          {single && (
            <Button variant="primary" onClick={() => openLead(single.leadId)}>
              View lead
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
